/* eslint-disable react/prop-types */
import { Button, CrashIcon, EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'
import { LoadingScreen } from '../../../components/generic'
import withToolbar from './withToolbar.old'
import { useState } from 'react'
import chartTitles from './chartTitles'

// HOC is used by ResidentChartScreen to render individual charts
type WithDataLoaderProps = {
  enableMockData: boolean
  errorMessage: string
  url: (props: any) => string
}

type DataLoaderProps = {
  mockDataFetch: any
  params: {
    resident_id: string
    chart_type: string
  }
}

type WrappedComponentProps = {
  mock: boolean
  data: any
}

export default function withDataLoader(
  WrappedComponent: (props: WrappedComponentProps) => JSX.Element,
  { enableMockData, errorMessage, url }: WithDataLoaderProps
) {
  const DataLoader = (props: DataLoaderProps) => {
    const [shouldRenderMockData, setShouldRenderMockData] = useState(false)
    const fetchUrl = url(props)

    const { data, isLoading, error } = useQuery<
      { result: any },
      { message: any }
    >([fetchUrl], () => getJSON({ url: fetchUrl }), {
      cacheTime: 0,
    })

    const base = url(props)
    const symb = base.match(/\?/) ? '&' : '?'
    const mockUrl = `${base}${symb}mock=1`

    const { data: mockedData, isLoading: isMockLoading } = useQuery<{
      result: any
    }>([mockUrl], () => getJSON({ url: mockUrl }), {
      enabled: shouldRenderMockData,
    })

    const renderError = () => (
      <EmptyState
        title={error?.message || errorMessage || 'Oops.. Something went wrong'}
        icon={<NoDataIcon />}
      />
    )

    const renderSuccess = (paramData: any, isMock?: any) => (
      <WrappedComponent
        mock={isMock}
        data={paramData || data?.result || []}
        {...props}
      />
    )

    if (isLoading || isMockLoading) {
      return <LoadingScreen />
    }

    if (enableMockData && mockedData) {
      return renderSuccess(mockedData.result, true)
    }

    if (data?.result) {
      const result = data?.result
      // Case: There's no data, but there is a sample
      if (enableMockData && !result?.length) {
        return (
          <SampleChartWithTitle
            onButtonClick={() => setShouldRenderMockData(true)}
            {...props}
          />
        )
      }

      // Case: There's no data, and there is no sample
      if (!enableMockData && !result?.length) {
        return <SampleChartWithTitle noSample={!result?.length} {...props} />
      }

      return renderSuccess(result)
    }

    if (error) {
      return renderError()
    }

    return (
      <EmptyState icon={<CrashIcon />} title="Oops.. Something went wrong" />
    )
  }

  return DataLoader
}

// TODO: all of HOC logic needs to be removed
const SampleChartWithTitle = (props: any) => {
  const Chart = withToolbar(
    ({ onButtonClick }: { onButtonClick: () => void }) => (
      <EmptyState title="You haven't added any logs yet" icon={<NoDataIcon />}>
        {!props?.noSample && (
          <Button onClick={() => onButtonClick()}>Show sample chart</Button>
        )}
      </EmptyState>
    ),
    {
      hideLogFilter: true,
      title:
        chartTitles[props?.params?.chart_type as keyof typeof chartTitles] ||
        'Chart',
    }
  )
  return <Chart {...props} />
}
